import React from 'react';
import Star from '@assets/icons/star';
import { useParams } from 'react-router-dom';
import { useIsJCUser } from '@hooks/user.hook';
import { subjectIdFromName } from '@utils/helpers/subjectIdFromName';

interface BaseTableItem {
  skill: string;
  marks: string;
}
interface ITableDataInfo extends BaseTableItem {
  alBanding: string;
}
interface ITableDataSecondaryInfo extends BaseTableItem {
  banding: string;
}

interface JCUserData extends BaseTableItem {
  grade: string;
}
export interface SkillLevelPopUpInfoProps {
  levelId: string;
  darkMode?: boolean;
}

type TableDataItem = JCUserData | ITableDataInfo | ITableDataSecondaryInfo;
const isJCUserData = (item: TableDataItem): item is JCUserData => {
  return 'grade' in item;
};

const isITableDataInfo = (item: TableDataItem): item is ITableDataInfo => {
  return 'alBanding' in item;
};

interface TableRowProps {
  skill: string;
  grade: string;
  marks: string;
}

const SkillLevelPopUpInfo = (props: SkillLevelPopUpInfoProps) => {
  const { subjectId: subjectName } = useParams();
  const { isJC } = useIsJCUser({
    subjectId: subjectIdFromName(subjectName ?? ''),
  });

  const tableData: ITableDataInfo[] = [
    { skill: '10', alBanding: 'AL1', marks: '90 - 100 marks' },
    { skill: '9', alBanding: 'AL2', marks: '85 - 89 marks' },
    { skill: '8', alBanding: 'AL3', marks: '80 - 84 marks' },
    { skill: '7', alBanding: 'AL4', marks: '75 - 79 marks' },
    { skill: '6', alBanding: 'AL5', marks: '65 - 74 marks' },
    { skill: '4 - 5', alBanding: 'AL6', marks: '45 - 64 marks' },
    { skill: '2 - 3', alBanding: 'AL7', marks: '20 - 44 marks' },
    { skill: '1', alBanding: 'AL8', marks: '<20 marks' },
  ];

  const tableDataSecondary: ITableDataSecondaryInfo[] = [
    { skill: '8 - 10', banding: 'A1', marks: '75 - 100 marks' },
    { skill: '7', banding: 'A2', marks: '70 - 74 marks' },
    { skill: '6', banding: 'B3', marks: '65 - 69 marks' },
    { skill: '5', banding: 'B4', marks: '60 - 64 marks' },
    { skill: '4', banding: 'C5', marks: '55 - 59 marks' },
    { skill: '3', banding: 'C6', marks: '50 - 54 marks' },
    { skill: '2', banding: 'E8 - D7', marks: '40 - 49 marks' },
    { skill: '1', banding: 'F9', marks: '<40 marks' },
  ];

  const tableDataJCUser: JCUserData[] = [
    { skill: '10', grade: 'A', marks: '86 - 100 marks' },
    { skill: '9', grade: 'A', marks: '70 - 85 marks' },
    { skill: '8', grade: 'B', marks: '60 - 69 marks' },
    { skill: '7', grade: 'C', marks: '55 - 59 marks' },
    { skill: '6', grade: 'D', marks: '50 - 54 marks' },
    { skill: '5', grade: 'E', marks: '45 - 49 marks' },
    { skill: '4', grade: 'S', marks: '40 - 44 marks' },
    { skill: '3', grade: 'U', marks: '35 - 39 marks' },
    { skill: '2', grade: 'U', marks: '21 - 34 marks' },
    { skill: '1', grade: 'U', marks: '0 - 20 marks' },
  ];

  const tableRow: React.FC<TableRowProps> = ({ skill, grade, marks }) => (
    <tr className="[&>td]:py-2">
      <td>
        <div className="flex justify-end items-center">
          <span className="w-1/2 text-right">{skill}</span>
          <span className="w-1/2">
            <Star className="fill-current text-alert-warning w-5 inline-block ml-1 align-baseline" />
          </span>
        </div>
      </td>
      <td className="pl-4 text-center">{grade}</td>
      <td className="pr-4 text-right">{marks}</td>
    </tr>
  );

  const tableBody = () => {
    const formatTableData = <T extends TableDataItem>(
      data: T[],
    ): TableRowProps[] => {
      return data.map(item => ({
        skill: item.skill,
        marks: item.marks,
        grade: isJCUserData(item)
          ? item.grade
          : isITableDataInfo(item)
          ? item.alBanding
          : item.banding,
      }));
    };

    const renderData = (): TableRowProps[] => {
      if (isJC) {
        return formatTableData(tableDataJCUser);
      }

      return Number(props.levelId) <= 6
        ? formatTableData(tableData)
        : formatTableData(tableDataSecondary);
    };

    return (
      <tbody>
        {renderData().map(item =>
          tableRow({ skill: item.skill, grade: item.grade, marks: item.marks }),
        )}
      </tbody>
    );
  };

  return (
    <div className={'text-neutral-pencil text-center'}>
      <div className={'mb-3'}>
        <table
          className={
            'w-full mx-auto min-lg:max-w-[380px] ' +
            (props.darkMode ? 'text-neutral-whiteboard' : '')
          }
          data-testid={'table-info'}
        >
          <thead>
            <tr
              className={
                'text-sm [&>th]:py-2 [&>th]:bg-neutral-sketchbook ' +
                (props.darkMode
                  ? 'text-neutral-whiteboard'
                  : 'text-primary-navyblue')
              }
              data-testid={'header-table'}
            >
              <th className={'pl-4 rounded-l-lg text-left'}>Skill Levels</th>
              <th className={'pl-[15px] text-center'}>
                {isJC ? 'Grade' : 'Banding'}
              </th>
              <th className={'pr-4 rounded-r-lg text-right'}>Marks</th>
            </tr>
          </thead>
          {tableBody()}
        </table>
      </div>
    </div>
  );
};

export default SkillLevelPopUpInfo;
